import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, catchError, throwError } from 'rxjs';
import { AliasSessionService } from '../services/alias-session.service';
import { AuthenticationService } from '../services/authentication.service';
import { HardReloadService } from '../services/hard-reload.service';

// Set by the backend's JWT / alias-refresh filter — NOT AliasReadOnlyFilter —
// when the alias SESSION itself is gone (explicit end, idle expiry, or simply
// missing), as opposed to a plain 401 for a refreshable access token that
// `auth.interceptor` already retries.
const ALIAS_SESSION_ENDED_HEADER = 'X-Alias-Session-Ended';

// Set by the backend's AliasReadOnlyFilter on a 403 raised specifically to
// block a mutating request during a read-only alias session — distinguishes
// that case from a genuine authz 403 (a resource the vendor legitimately
// can't access), which must pass through untouched.
const READ_ONLY_ALIAS_BLOCK_HEADER = 'X-Read-Only-Alias-Block';

/**
 * Global functional interceptor covering the two staff-alias-session error
 * shapes surfaced by the backend (see `AliasSessionService`, Task 13):
 *
 * - A 401 carrying `X-Alias-Session-Ended: true` means the alias session
 *   itself has ended — there's no alias session left to refresh into, so
 *   this takes precedence over both the 403 branch below and
 *   `auth.interceptor`'s normal 401 refresh-retry (registered before this one
 *   — see `app.config.ts` — so this interceptor sees the raw error first on
 *   the way back up and can short-circuit before a refresh is attempted).
 *   Clears local alias state, re-confirms `/user/me` to re-store the STAFF
 *   profile (the cache still holds the aliased VENDOR profile at this point —
 *   see `AliasBannerComponent.refreshStaffSessionAndReload`, which this
 *   mirrors), then hard-reloads back to the internal dashboard so the whole
 *   app re-bootstraps as the staffer.
 * - A 403 carrying `X-Read-Only-Alias-Block: true` means the backend's
 *   `AliasReadOnlyFilter` blocked a mutating request — surfaced as a
 *   friendly "read-only guidance mode" toast instead of a raw error. Gated on
 *   that header rather than `AliasSessionService.isAliased()`, so a genuine
 *   authz 403 (a resource the vendor legitimately can't access) is untouched
 *   even during an alias session. There's nothing left for the calling
 *   component to handle here, so this branch swallows the error (`EMPTY`)
 *   instead of rethrowing it — otherwise the component's own
 *   `.subscribe({ error })` would also fire and stack a generic error toast
 *   on top of this friendly warning.
 *
 * The session-ended branch still rethrows after triggering its reload, so
 * existing per-call `.subscribe({ error })` handling downstream still runs
 * unchanged for that case; the read-only branch above is the one exception,
 * layering its toast on top and ending the stream there.
 */
export const aliasInterceptor: HttpInterceptorFn = (req, next) => {
  const aliasSessionService = inject(AliasSessionService);
  const authService = inject(AuthenticationService);
  const toastr = inject(ToastrService);
  const hardReload = inject(HardReloadService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        error.headers.get(ALIAS_SESSION_ENDED_HEADER) === 'true'
      ) {
        // Re-entry into this branch is prevented SERVER-side, not by the
        // clear() below: the backend's JwtAuthenticationFilter.endAliasOverlay()
        // clears the alias cookies on this same 401, so the me() call below is
        // sent with only the STAFF cookies — it can't produce another
        // X-Alias-Session-Ended 401. (auth.interceptor also declines to refresh
        // a session-ended 401, so there's no racy staff refresh either.) The
        // clear() here is a local-state cleanup — it drops the stale aliased
        // signal immediately so the UI doesn't flash alias affordances during
        // the brief window before the hard-reload — NOT the loop guard. me()
        // resolves as the STAFF user; on failure, reload anyway rather than
        // stranding the staffer.
        aliasSessionService.clear();
        toastr.warning('Your guidance session ended after inactivity.');
        authService.me().subscribe({
          next: () => hardReload.to('/internal/dashboard'),
          error: () => hardReload.to('/internal/dashboard'),
        });
        return throwError(() => error);
      }

      if (
        error.status === 403 &&
        error.headers.get(READ_ONLY_ALIAS_BLOCK_HEADER) === 'true'
      ) {
        toastr.warning("This action isn't available in read-only guidance mode.");
        // EMPTY completes without erroring, so the caller's own error toast
        // never stacks on top of this warning. Two caveats for future callers:
        // (1) a component that resets loading state ONLY in `.subscribe({ error })`
        //     (not `next`/`complete`/`finalize`) won't reset it on a read-only
        //     block — prefer `finalize()` for spinner/button teardown.
        // (2) a mutating call awaited via `firstValueFrom`/`lastValueFrom` will
        //     reject with RxJS `EmptyError`, NOT an HttpErrorResponse — a `catch`
        //     that assumes the latter must also handle EmptyError. No current
        //     caller hits either path (all mutating awaits are refresh-exempt),
        //     but keep this in mind when adding `await http.post(...)` reachable
        //     during an alias session.
        return EMPTY;
      }

      return throwError(() => error);
    })
  );
};
